import type {
  BuyerClearFlagKey,
  BuyerEquitySource,
  BuyerLeadDetailDto,
  BuyerOperatorArrangement,
  BuyerType,
  UpdateBuyerLeadPayload,
} from "@/api/buyers";

/**
 * ONE config drives both the read card and the edit modal.
 *
 * <p>The shape follows <c>profile/intakeSectionConfig.ts</c> — the loan Profile
 * tab's carried-intake sections — deliberately, over the alternative precedent
 * (<c>QualificationsProfileTab</c>'s hand-written <c>ProfileSection</c> plus a
 * hand-written drawer). Two hand-written renderings of one field set drift, and
 * the one that drifts is whichever nobody is looking at: a label changes on the
 * card and not in the drawer, or a field is added to the drawer and never
 * appears on the record. Here the card and the modal are two folds over the same
 * array, so a field cannot exist in one and not the other.</p>
 *
 * <p>🔴 <b>The keys are the WIRE keys.</b> Each <c>key</c> is simultaneously a
 * property of {@link BuyerLeadDetailDto} (what the card reads) and of
 * {@link UpdateBuyerLeadPayload} (what the modal sends). That is what makes the
 * two halves provably the same field rather than two strings that happen to
 * match today — the types below are constrained to the intersection, so a typo
 * or a rename fails the typecheck instead of silently editing nothing.</p>
 */

/** Keys a section may both render and patch. */
export type BuyerEditableKey = Extract<
  keyof UpdateBuyerLeadPayload,
  keyof BuyerLeadDetailDto
>;

/** The `clear*` flags on the patch — a bare boolean, never a field. */
export type BuyerClearFlag = BuyerClearFlagKey;

export type BuyerFieldType =
  | "text"
  | "email"
  | "tel"
  | "textarea"
  | "money"
  | "int"
  | "date"
  | "yesno"
  | "select";

/** One choice of a `select` field: the wire value and what a reader sees. */
export interface BuyerSelectOption {
  value: string;
  label: string;
}

/**
 * Show a field only when another field of the record holds one of `anyOf`.
 *
 * <p>The condition is read from the FORM when the deciding field is in the
 * same section (so the contact modal reveals the company rows the moment the
 * buyer type changes), otherwise from the record. 🔴 A hidden field is OMITTED
 * from the patch — never sent as `""` — so switching a buyer back to
 * Individual leaves their company details on file rather than erasing them.</p>
 */
export interface BuyerVisibleWhen {
  key: BuyerEditableKey;
  anyOf: readonly string[];
  /**
   * Read the deciding value from the RECORD even when the form holds the key.
   * A rule on a field's OWN stored value (the full-time operator row shows for
   * an individual who has one on file) must not hide the row the moment the
   * input is set back to "Not stated" mid-edit.
   */
  fromRecord?: boolean;
}

/**
 * The choices a `select` offers in the MODAL when they depend on another field:
 * `options` when `when` holds, else `otherwise`. The card keeps labelling from
 * the field's full `options`, so a stored value is always printed as its label.
 */
export interface BuyerOfferedOptions {
  when: BuyerVisibleWhen;
  options: readonly BuyerSelectOption[];
  otherwise: readonly BuyerSelectOption[];
}

export interface BuyerFieldConfig {
  key: BuyerEditableKey;
  label: string;
  type: BuyerFieldType;
  /**
   * 🔴 Required for every numeric and every <c>bool?</c> field, and forbidden on
   * strings.
   *
   * <p>The patch is FIND-12: bare <c>null</c> means "unchanged" and <c>""</c>
   * means "clear". A string can therefore express its own erasure. A number
   * cannot — there is no empty number — so blanking a figure was simply
   * unexpressible over this API until these flags existed, and the field silently
   * kept its old value. Same for a tri-state bool, where "the buyer never said"
   * is a real answer and <c>false</c> is a different one.</p>
   */
  clearFlag?: BuyerClearFlag;
  /**
   * 🔴 Rendered on the CARD, never offered in the MODAL.
   *
   * <p>This is the one asymmetry the "one config drives both" rule above
   * permits, and it exists because display and editability are different
   * questions about a field: something captured at intake is a record worth
   * reading and not something a specialist should retype, because a hand-edited
   * value is a fabricated provenance that reporting cannot tell from a captured
   * one. (The UTM/campaign block that first needed it was retired by D2; the
   * flag stays for the next captured-not-typed field.)</p>
   *
   * <p>It does not reintroduce the drift the rule guards against. The flag is
   * declared once, in the same array both folds read, so a field still cannot
   * exist in one and not the other — it can only be shown-but-not-asked. The
   * direction that would matter, editable-but-undisplayed, remains impossible.</p>
   */
  readOnly?: boolean;
  /** Full-width in the modal's two-column grid. */
  wide?: boolean;
  /** Placeholder / format hint on the input. */
  hint?: string;
  /** The choices of a `select` field; the card prints the label, never the
   *  stored token. The blank choice ("Not stated") sends the `clearFlag`. */
  options?: readonly BuyerSelectOption[];
  /** See {@link BuyerVisibleWhen}. Absent means always shown; a list shows the
   *  field when ANY one rule holds. */
  visibleWhen?: BuyerVisibleWhen | readonly BuyerVisibleWhen[];
  /** See {@link BuyerOfferedOptions}. Absent means the modal offers `options`. */
  offeredOptions?: BuyerOfferedOptions;
  /** The input's character limit, where the server column has one. */
  maxLength?: number;
}

export type BuyerSectionId =
  | "contact"
  | "capacity"
  | "criteria"
  | "background"
  | "attribution"
  | "record-note";

// ── Company & sponsor buyers (owner brief 2026-10-08) ──────────────────────
// Staff-facing labels, the same words as `BuyerAcquirer.BuyerTypeLabel`,
// `EquitySourceLabel` and `OperatorArrangementLabel` on the server. Exhaustive
// `Record`s, so a member added to a union fails typecheck here instead of
// painting a raw token on the card.

export const BUYER_TYPE_LABELS: Record<BuyerType, string> = {
  Individual: "Individual",
  CompanyOwnedOrLed: "A company they own or lead",
  InvestmentGroupOrFund: "An investment group or fund they represent",
};

export const EQUITY_SOURCE_LABELS: Record<BuyerEquitySource, string> = {
  CompanyFundsOnHand: "Company funds on hand",
  CommittedByInvestors: "Committed by investors or partners",
  StillBeingRaised: "Still being raised (subject to final capitalization)",
};

export const OPERATOR_ARRANGEMENT_LABELS: Record<BuyerOperatorArrangement, string> = {
  CompanyPrincipal: "A principal of the acquiring company",
  HiredGeneralManager: "A general manager to be hired",
  ExistingManagement: "Existing management will stay",
  DesignatedPerson: "Another person they designate",
};

/** The arrangements a COMPANY buyer can answer (D3: a company keeps its three;
 *  `DesignatedPerson` is an individual's answer only and the server drops it
 *  for a company). Filtered from the exhaustive map, so a new member still
 *  needs a label above. */
const COMPANY_OPERATOR_ARRANGEMENT_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(OPERATOR_ARRANGEMENT_LABELS).filter(([value]) => value !== "DesignatedPerson"),
);

/** The arrangements an INDIVIDUAL overseer can answer (FT, Q8): a general
 *  manager to be hired, existing management, or another person they designate.
 *  A company principal is a company's answer only. */
const INDIVIDUAL_OPERATOR_ARRANGEMENT_LABELS: Record<string, string> = Object.fromEntries(
  Object.entries(OPERATOR_ARRANGEMENT_LABELS).filter(([value]) => value !== "CompanyPrincipal"),
);

function optionsOf(labels: Record<string, string>): readonly BuyerSelectOption[] {
  return Object.entries(labels).map(([value, label]) => ({ value, label }));
}

/** The company rows show for a company TYPE — not `isCompanyBuyer`, which also
 *  needs a name: a specialist must be able to see the blank name to fill it. */
const WHEN_COMPANY_TYPE: BuyerVisibleWhen = {
  key: "buyerType",
  anyOf: ["CompanyOwnedOrLed", "InvestmentGroupOrFund"],
};

/** FT (Q8): the full-time operator rows also show for a buyer who has an
 *  arrangement ON FILE — an individual overseer's prequalification writes one
 *  through. The record carries no involvement answer, so a recorded
 *  arrangement is the one predicate the record can answer. */
const WHEN_OPERATOR_ARRANGEMENT_ON_FILE: BuyerVisibleWhen = {
  key: "operatorArrangement",
  anyOf: Object.keys(OPERATOR_ARRANGEMENT_LABELS),
  fromRecord: true,
};

/** `BuyerAcquirer.MaxAboutLength` — "About the company" is two to four
 *  sentences. The server refuses a longer value either way. */
export const ABOUT_THE_COMPANY_MAX_LENGTH = 600;

export interface BuyerSectionConfig {
  id: BuyerSectionId;
  title: string;
  /** Sentence shown at the top of the edit modal. */
  editDescription: string;
  fields: readonly BuyerFieldConfig[];
}

/**
 * The five editable sections, plus the record's own note field.
 *
 * <p>Ownership and status are deliberately absent from every one of them.
 * Reassignment writes the grant ledger and a status change stamps a milestone,
 * so both move through their own endpoints — the server's patch does not accept
 * either, and offering them in a section modal would invite a silent no-op.</p>
 */
export const BUYER_SECTIONS: readonly BuyerSectionConfig[] = [
  {
    id: "contact",
    title: "Contact",
    editDescription:
      "How to reach this buyer. The assigned consultant is changed from the assignment queue, not here.",
    fields: [
      { key: "firstName", label: "First name", type: "text" },
      { key: "lastName", label: "Last name", type: "text" },
      { key: "email", label: "Email", type: "email" },
      { key: "phone", label: "Phone", type: "tel" },
      { key: "city", label: "City", type: "text" },
      { key: "state", label: "State", type: "text" },
      { key: "zip", label: "ZIP", type: "text" },
      // Who is buying. The company rows appear only for a company type, and a
      // hidden row is left out of the patch (see BuyerVisibleWhen).
      {
        key: "buyerType",
        label: "Buyer type",
        type: "select",
        options: optionsOf(BUYER_TYPE_LABELS),
        clearFlag: "clearBuyerType",
      },
      {
        key: "acquiringCompanyName",
        label: "Acquiring company",
        type: "text",
        visibleWhen: WHEN_COMPANY_TYPE,
      },
      {
        key: "acquiringCompanyTitle",
        label: "Title at the company",
        type: "text",
        hint: "e.g. CEO",
        visibleWhen: WHEN_COMPANY_TYPE,
      },
      {
        key: "acquiringCompanyWebsite",
        label: "Company website",
        type: "text",
        hint: "https://",
        visibleWhen: WHEN_COMPANY_TYPE,
      },
      {
        key: "acquiringCompanyAbout",
        label: "About the company",
        type: "textarea",
        wide: true,
        maxLength: ABOUT_THE_COMPANY_MAX_LENGTH,
        hint: "Two to four sentences, in the buyer's words.",
        visibleWhen: WHEN_COMPANY_TYPE,
      },
      // The next follow-up is no longer a field on the record — a follow-up is
      // a WorkTask, scheduled from the next-step strip in the header.
    ],
  },
  {
    id: "capacity",
    title: "Financial capacity",
    editDescription:
      "What this buyer can bring. Leave a figure blank to remove it — a wrong number is worse than none.",
    fields: [
      { key: "liquidCapital", label: "Liquid capital", type: "money", clearFlag: "clearLiquidCapital" },
      { key: "netWorth", label: "Net worth", type: "money", clearFlag: "clearNetWorth" },
      {
        key: "maxPurchasePrice",
        label: "Max purchase price",
        type: "money",
        clearFlag: "clearMaxPurchasePrice",
      },
      {
        key: "downPaymentAvailable",
        label: "Down payment available",
        type: "money",
        clearFlag: "clearDownPaymentAvailable",
      },
      {
        key: "equitySource",
        label: "Equity source",
        type: "select",
        options: optionsOf(EQUITY_SOURCE_LABELS),
        clearFlag: "clearEquitySource",
        visibleWhen: WHEN_COMPANY_TYPE,
      },
      {
        key: "creditScoreBand",
        label: "Credit score band",
        type: "int",
        clearFlag: "clearCreditScoreBand",
        hint: "e.g. 720",
      },
    ],
  },
  {
    id: "criteria",
    title: "Acquisition criteria",
    editDescription: "What this buyer is looking for. Blank means we have not been told.",
    fields: [
      { key: "targetIndustries", label: "Target industries", type: "text", wide: true },
      { key: "targetGeographies", label: "Target geographies", type: "text", wide: true },
      {
        key: "targetRevenueMin",
        label: "Target revenue (min)",
        type: "money",
        clearFlag: "clearTargetRevenueMin",
      },
      {
        key: "targetRevenueMax",
        label: "Target revenue (max)",
        type: "money",
        clearFlag: "clearTargetRevenueMax",
      },
      {
        key: "targetCashFlowMin",
        label: "Min cash flow",
        type: "money",
        clearFlag: "clearTargetCashFlowMin",
      },
      { key: "timelineToPurchase", label: "Timeline", type: "text" },
      { key: "relocationWillingness", label: "Will relocate", type: "text" },
    ],
  },
  {
    id: "background",
    title: "Background & financing",
    editDescription:
      "Experience and how the purchase would be funded. SBA is a three-way answer — leave it unset when the buyer has not said.",
    fields: [
      { key: "industryExperience", label: "Industry experience", type: "textarea", wide: true },
      { key: "managementExperience", label: "Management experience", type: "textarea", wide: true },
      // Who will manage the business full time when the buyer will not: a
      // company's three answers (owner ruling Q8; the interview's MF3b answer
      // writes here), or an individual overseer's designated operator (FT, Q8 —
      // the prequalification writes it through). The card labels every member;
      // the modal offers the buyer type's own choices.
      {
        key: "operatorArrangement",
        label: "Full-time operator",
        type: "select",
        options: optionsOf(OPERATOR_ARRANGEMENT_LABELS),
        offeredOptions: {
          when: WHEN_COMPANY_TYPE,
          options: optionsOf(COMPANY_OPERATOR_ARRANGEMENT_LABELS),
          otherwise: optionsOf(INDIVIDUAL_OPERATOR_ARRANGEMENT_LABELS),
        },
        clearFlag: "clearOperatorArrangement",
        visibleWhen: [WHEN_COMPANY_TYPE, WHEN_OPERATOR_ARRANGEMENT_ON_FILE],
      },
      {
        key: "operatorName",
        label: "Operator's name",
        type: "text",
        visibleWhen: [WHEN_COMPANY_TYPE, WHEN_OPERATOR_ARRANGEMENT_ON_FILE],
      },
      { key: "isSbaCandidate", label: "SBA candidate", type: "yesno", clearFlag: "clearIsSbaCandidate" },
      { key: "financingNotes", label: "Financing notes", type: "textarea", wide: true },
    ],
  },
  {
    id: "attribution",
    title: "Attribution",
    editDescription:
      "Source detail is the only line here anyone types. Lead source is edited on the card itself, because it saves as soon as it changes; the advertisement is captured at intake and shown read-only.",
    fields: [
      { key: "sourceDetail", label: "Source detail", type: "text", wide: true },
      // The campaign string and the nine UTM/ad fields that were rendered
      // read-only here were retired by D2 — the advertisement IS the
      // attribution, and the raw query-string capture lives on the
      // prequalification submission, not the lead.

    ],
  },
  {
    id: "record-note",
    title: "Notes on file",
    editDescription:
      "The note field on this buyer's record, captured at intake. Team discussion belongs in the composer above.",
    fields: [
      { key: "notes", label: "Notes on file", type: "textarea", wide: true },
    ],
  },
];

export function buyerSection(id: BuyerSectionId): BuyerSectionConfig {
  const found = BUYER_SECTIONS.find((s) => s.id === id);
  // Unreachable via the type, but a thrown error beats rendering an empty card
  // that looks like a record with no data in it.
  if (!found) throw new Error(`Unknown buyer section: ${id}`);
  return found;
}

/**
 * Every value the form holds is a STRING, because every input returns one.
 *
 * <p>The conversion happens once, on the way out, in {@link buildSectionPatch} —
 * not per input. A form that stores numbers has to decide what a half-typed
 * "1.2e" is on every keystroke; a form that stores strings decides once, at the
 * only moment it matters.</p>
 */
export type BuyerSectionForm = Record<string, string>;

/** Hydrate one section's inputs from the record. */
/**
 * The subset of a section a person may type into.
 *
 * <p>Read by the modal's renderer AND by all three form helpers below, so a
 * read-only field is never hydrated into the form, never compared for
 * dirtiness, and never lands in a patch. Filtering only at the render site
 * would leave a hydrated value in state that a blank-means-clear rule could
 * still send.</p>
 */
export function editableFields(
  section: BuyerSectionConfig,
): readonly BuyerFieldConfig[] {
  return section.fields.filter((f) => !f.readOnly);
}

/**
 * Is this field shown? Always, unless it carries a `visibleWhen` — then the
 * deciding value comes from `form` when the form holds that key (the same
 * section), otherwise from the record.
 */
export function fieldIsVisible(
  field: BuyerFieldConfig,
  buyer: BuyerLeadDetailDto,
  form?: BuyerSectionForm,
): boolean {
  const rules = field.visibleWhen;
  if (!rules) return true;
  const list: readonly BuyerVisibleWhen[] = Array.isArray(rules) ? rules : [rules as BuyerVisibleWhen];
  return list.some((rule) => ruleHolds(rule, buyer, form));
}

function ruleHolds(
  rule: BuyerVisibleWhen,
  buyer: BuyerLeadDetailDto,
  form?: BuyerSectionForm,
): boolean {
  const value =
    !rule.fromRecord && form !== undefined && rule.key in form ? form[rule.key] : buyer[rule.key];
  return value !== null && value !== undefined && rule.anyOf.includes(String(value));
}

/** The choices the modal offers for a `select` — see {@link BuyerOfferedOptions}. */
export function offeredOptions(
  field: BuyerFieldConfig,
  buyer: BuyerLeadDetailDto,
  form?: BuyerSectionForm,
): readonly BuyerSelectOption[] | undefined {
  const offered = field.offeredOptions;
  if (!offered) return field.options;
  return ruleHolds(offered.when, buyer, form) ? offered.options : offered.otherwise;
}

/**
 * The fields a person can type into RIGHT NOW: editable and visible. The modal
 * renders these, and the patch and the dirty check read the same subset, so a
 * hidden field is never sent and never makes Save light up.
 */
export function visibleEditableFields(
  section: BuyerSectionConfig,
  buyer: BuyerLeadDetailDto,
  form: BuyerSectionForm,
): readonly BuyerFieldConfig[] {
  // `options` resolved to what THIS buyer is offered, so the modal renders the
  // buyer type's own choices without knowing the rule.
  return editableFields(section)
    .filter((f) => fieldIsVisible(f, buyer, form))
    .map((f) => (f.offeredOptions ? { ...f, options: offeredOptions(f, buyer, form) } : f));
}

export function hydrateSection(
  section: BuyerSectionConfig,
  buyer: BuyerLeadDetailDto,
): BuyerSectionForm {
  // Hidden fields are hydrated too, so a row revealed mid-edit (the buyer type
  // switched to a company) shows what is already on file rather than a blank.
  const form: BuyerSectionForm = {};
  for (const field of editableFields(section)) {
    const value = buyer[field.key];
    form[field.key] =
      value === null || value === undefined
        ? ""
        : field.type === "yesno"
          ? value === true
            ? "yes"
            : "no"
          : field.type === "date"
            ? isoToDateInput(String(value))
            : String(value);
  }
  return form;
}

/** `2026-07-01T00:00:00Z` → `2026-07-01`, which is what `<input type="date">` wants. */
function isoToDateInput(iso: string): string {
  return iso.length >= 10 ? iso.slice(0, 10) : "";
}

/**
 * Turn one section's form into the narrowest patch that expresses it.
 *
 * <p>Only this section's keys are sent. Omitted means unchanged, which is exactly
 * what a section edit means about every field it does not contain — so a
 * specialist correcting a phone number cannot accidentally re-assert a stale
 * figure they never looked at. That is the FIND-12 contract used as designed
 * rather than worked around by hydrating the whole record.</p>
 */
export function buildSectionPatch(
  section: BuyerSectionConfig,
  form: BuyerSectionForm,
  buyer: BuyerLeadDetailDto,
): UpdateBuyerLeadPayload {
  const patch: Record<string, unknown> = {};

  // 🔴 Visible fields only. A hidden field is OMITTED (unchanged), never sent
  // as "" — see BuyerVisibleWhen.
  for (const field of visibleEditableFields(section, buyer, form)) {
    const raw = (form[field.key] ?? "").trim();
    const blank = raw.length === 0;

    switch (field.type) {
      case "money":
      case "int": {
        if (blank) {
          // 🔴 The flag, not `null`. Null reads as "unchanged" server-side, so
          // sending it here would silently keep the figure the user just erased.
          if (field.clearFlag) patch[field.clearFlag] = true;
          break;
        }
        const parsed = field.type === "int" ? Number.parseInt(raw, 10) : Number.parseFloat(raw);
        // A value that is not a number is dropped rather than sent as NaN, which
        // serialises to `null` and would read as "unchanged" — the same silent
        // no-op the clear flags exist to end.
        if (Number.isFinite(parsed)) patch[field.key] = parsed;
        break;
      }
      case "yesno": {
        if (blank) {
          if (field.clearFlag) patch[field.clearFlag] = true;
          break;
        }
        patch[field.key] = raw === "yes";
        break;
      }
      case "date":
      case "select": {
        // A select's blank choice is "Not stated", which clears through the
        // flag exactly like a blank date — null would read as "unchanged".
        if (blank) {
          if (field.clearFlag) patch[field.clearFlag] = true;
          break;
        }
        patch[field.key] = raw;
        break;
      }
      default:
        // Strings say "clear" with "" all on their own — FIND-12 — so a blank
        // one is sent verbatim rather than flagged.
        patch[field.key] = raw;
    }
  }

  return patch as UpdateBuyerLeadPayload;
}

/** Has anything in this section actually changed? Keeps Save honest. */
export function sectionIsDirty(
  section: BuyerSectionConfig,
  buyer: BuyerLeadDetailDto,
  form: BuyerSectionForm,
): boolean {
  const original = hydrateSection(section, buyer);
  return visibleEditableFields(section, buyer, form).some(
    (f) => (form[f.key] ?? "").trim() !== (original[f.key] ?? "").trim(),
  );
}
